/**
 * Shown to a signed-in user whose platform access has not been configured.
 *
 * The backend gates every protected endpoint on access_state (see
 * apps/core/permissions.py → HasPlatformAccess) and answers 403 with a
 * machine-readable code plus a blocker list. Without this page those 403s
 * surfaced as a generic error toast on a half-rendered app shell, which told
 * the user nothing and told them nothing about who could fix it.
 *
 * "Check again" exists because the backend self-heals: a stale projection is
 * re-derived on the deny path, so a user who was wrongly blocked can often
 * clear themselves without waiting for an administrator.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, RefreshCw, LogOut, CalendarX, UserX } from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';

// Blocker key → what a non-technical user should read. Anything unrecognised
// falls through to a generic line rather than leaking the raw key.
const BLOCKER_COPY = {
  missing_role: {
    Icon: UserX,
    title: 'No role has been assigned to your account',
    detail:
      'Your district administrator needs to assign you a role before you can '
      + 'use the platform. This is usually done when your account is set up.',
  },
  no_schedule: {
    Icon: CalendarX,
    title: 'No work schedule has been assigned',
    detail:
      'Your administrator needs to assign you a work schedule before you can '
      + 'clock in or submit time-off and overtime requests.',
  },
};

const GENERIC_BLOCKER = {
  Icon: ShieldAlert,
  title: 'Your account setup is incomplete',
  detail: 'Your district administrator needs to finish setting up your access.',
};

const AccessPending = () => {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = React.useState(false);
  const [stillBlocked, setStillBlocked] = React.useState(false);

  const blockers = user?.access_blockers?.length
    ? user.access_blockers
    : ['missing_role'];

  const handleCheckAgain = async () => {
    setChecking(true);
    setStillBlocked(false);
    try {
      const { api } = await import('../lib/api');
      const response = await api.get('/rbac/me/');
      const { access_state: accessState, access_blockers: accessBlockers } =
        response.data || {};
      // Keep the cached user in step with the server, or ProtectedRoute will
      // bounce straight back here on the next render.
      setUser((previous) => (previous
        ? { ...previous, access_state: accessState, access_blockers: accessBlockers || [] }
        : previous));
      if (accessState === 'ready') {
        navigate('/dashboard', { replace: true });
        return;
      }
      setStillBlocked(true);
    } catch {
      setStillBlocked(true);
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12">
      <div className="w-full max-w-lg rounded-2xl bg-white p-8 shadow-sm border border-slate-200">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-5">
            <ShieldAlert className="w-8 h-8 text-amber-600" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Your access isn&apos;t set up yet
          </h1>
          <p className="text-slate-500 max-w-md">
            You&apos;re signed in
            {user?.email ? <> as <span className="font-medium text-slate-700">{user.email}</span></> : null}
            , but your district administrator hasn&apos;t finished configuring
            your account.
          </p>
        </div>

        <ul className="mt-7 space-y-3">
          {blockers.map((key) => {
            const { Icon, title, detail } = BLOCKER_COPY[key] || GENERIC_BLOCKER;
            return (
              <li
                key={key}
                className="flex gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4"
              >
                <Icon className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <p className="font-medium text-slate-900">{title}</p>
                  <p className="text-sm text-slate-500 mt-1">{detail}</p>
                </div>
              </li>
            );
          })}
        </ul>

        <p className="mt-6 text-sm text-slate-500 text-center">
          Contact your district administrator or IT department to have this
          completed. Nothing is wrong with your sign-in.
        </p>

        {stillBlocked && (
          <p
            role="status"
            className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800 text-center"
          >
            Your access still isn&apos;t configured. Please check back after
            your administrator has updated your account.
          </p>
        )}

        <div className="mt-7 flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={handleCheckAgain}
            disabled={checking}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 font-medium text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <RefreshCw
              className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
            {checking ? 'Checking…' : 'Check again'}
          </button>
          <button
            type="button"
            onClick={logout}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-4 py-2.5 font-medium text-slate-700 hover:bg-slate-50"
          >
            <LogOut className="w-4 h-4" aria-hidden="true" />
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccessPending;
